import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft,
    Boxes,
    ChevronDown,
    ChevronUp,
    Eye,
    EyeOff,
    Grid3X3,
    Layers,
    LayoutGrid,
    List,
    Package,
    RotateCcw,
    Scissors,
    SlidersHorizontal,
} from 'lucide-react';
import { useState } from 'react';
import ServiceCreateForm from './ServiceCreateForm';
import ComboCreateForm from './ComboCreateForm';
import useCreateCardsStore, { selectOrderedCards } from '../../../stores/createCardsStore';
import styles from '../../../assets/scss/pages/ServiceCreate.module.scss';

// Metadata 4 card (icon/màu/nội dung). Store chỉ giữ key/visible/order + layout.
const CARD_META = {
    dichvu: {
        title: 'Dịch vụ',
        desc: 'Tạo dịch vụ mà cửa hàng của bạn cung cấp cho khách hàng',
        icon: <Scissors />,
        iconClass: 'choiceIconBlue',
    },
    sanpham: {
        title: 'Sản phẩm',
        desc: 'Tạo sản phẩm bán lẻ, quản lý tồn kho và giá bán',
        icon: <Package />,
        iconClass: 'choiceIconGreen',
    },
    combo: {
        title: 'Combo',
        desc: 'Kết hợp nhiều dịch vụ và sản phẩm thành một gói ưu đãi',
        icon: <Layers />,
        iconClass: 'choiceIconPurple',
    },
    goidichvu: {
        title: 'Gói dịch vụ',
        desc: 'Gói dịch vụ nhiều buổi, bán theo thẻ cho khách dùng dần',
        icon: <Boxes />,
        iconClass: 'choiceIconOrange',
    },
};

const LAYOUT_OPTS = [
    { key: 'list', icon: <List size={16} />, label: 'Danh sách' },
    { key: 'grid', icon: <LayoutGrid size={16} />, label: 'Lưới to' },
    { key: 'compact', icon: <Grid3X3 size={16} />, label: 'Lưới nhỏ' },
];

export default function ServiceCreate() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const type = searchParams.get('type'); // dichvu | sanpham | combo | null

    const layout = useCreateCardsStore((s) => s.layout);
    const setLayout = useCreateCardsStore((s) => s.setLayout);
    const cards = useCreateCardsStore((s) => s.cards);
    const toggleVisible = useCreateCardsStore((s) => s.toggleVisible);
    const moveCard = useCreateCardsStore((s) => s.moveCard);
    const resetCards = useCreateCardsStore((s) => s.resetCards);

    const [editing, setEditing] = useState(false);

    const handleSelectCard = (key) => {
        if (key === 'goidichvu') navigate('/admin/packages/create');
        else setSearchParams({ type: key });
    };

    // Nếu chưa chọn type → hiện trang chọn
    if (!type) {
        const visibleCards = selectOrderedCards(cards);
        const allOrdered = selectOrderedCards(cards, { includeHidden: true });
        const layoutClass =
            layout === 'list'
                ? styles.choiceGridList
                : layout === 'compact'
                  ? styles.choiceGridCompact
                  : styles.choiceGridGrid;
        const cardClass =
            layout === 'list'
                ? styles.choiceCardList
                : layout === 'compact'
                  ? styles.choiceCardCompact
                  : '';

        return (
            <div className={styles.choicePage}>
                <div className={styles.choiceHeader}>
                    <h1 className={styles.choiceTitle}>Tạo mới</h1>
                    <p className={styles.choiceSub}>Chọn loại bạn muốn tạo</p>
                </div>

                {/* Thanh công cụ: chọn layout + nút tuỳ chỉnh */}
                <div className={styles.choiceToolbar}>
                    <div className={styles.choiceLayoutSwitch}>
                        {LAYOUT_OPTS.map((o) => (
                            <button
                                key={o.key}
                                className={`${styles.choiceLayoutBtn}${layout === o.key ? ` ${styles.choiceLayoutBtnActive}` : ''}`}
                                onClick={() => setLayout(o.key)}
                                title={o.label}
                            >
                                {o.icon}
                                <span>{o.label}</span>
                            </button>
                        ))}
                    </div>
                    <button
                        className={`${styles.choiceCustomizeBtn}${editing ? ` ${styles.choiceCustomizeBtnActive}` : ''}`}
                        onClick={() => setEditing((v) => !v)}
                    >
                        <SlidersHorizontal size={16} /> Tuỳ chỉnh
                    </button>
                </div>

                {/* Bảng tuỳ chỉnh ẩn/hiện + thứ tự */}
                {editing && (
                    <div className={styles.choiceEditPanel}>
                        <div className={styles.choiceEditHead}>
                            <span>Tuỳ chỉnh hiển thị &amp; thứ tự</span>
                            <button className={styles.choiceResetBtn} onClick={resetCards}>
                                <RotateCcw size={14} /> Mặc định
                            </button>
                        </div>
                        {allOrdered.map((c, idx) => {
                            const meta = CARD_META[c.key];
                            return (
                                <div key={c.key} className={styles.choiceEditRow}>
                                    <div className={`${styles.choiceEditIcon} ${styles[meta.iconClass]}`}>
                                        {meta.icon}
                                    </div>
                                    <span className={styles.choiceEditName}>{meta.title}</span>
                                    <div className={styles.choiceEditActions}>
                                        <button
                                            className={styles.choiceEditBtn}
                                            disabled={idx === 0}
                                            onClick={() => moveCard(c.key, -1)}
                                            title="Lên"
                                        >
                                            <ChevronUp size={16} />
                                        </button>
                                        <button
                                            className={styles.choiceEditBtn}
                                            disabled={idx === allOrdered.length - 1}
                                            onClick={() => moveCard(c.key, 1)}
                                            title="Xuống"
                                        >
                                            <ChevronDown size={16} />
                                        </button>
                                        <button
                                            className={`${styles.choiceEditBtn}${c.visible ? ` ${styles.choiceEditBtnOn}` : ''}`}
                                            onClick={() => toggleVisible(c.key)}
                                            title={c.visible ? 'Đang hiện' : 'Đang ẩn'}
                                        >
                                            {c.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {visibleCards.length === 0 ? (
                    <div className={styles.choiceEmpty}>
                        Đã ẩn tất cả. Bấm “Tuỳ chỉnh” để bật lại card.
                    </div>
                ) : (
                    <div className={`${styles.choiceGrid} ${layoutClass}`}>
                        {visibleCards.map((c) => {
                            const meta = CARD_META[c.key];
                            return (
                                <div
                                    key={c.key}
                                    className={`${styles.choiceCard} ${cardClass}`}
                                    onClick={() => handleSelectCard(c.key)}
                                >
                                    <div className={`${styles.choiceIcon} ${styles[meta.iconClass]}`}>
                                        {meta.icon}
                                    </div>
                                    <div className={styles.choiceCardBody}>
                                        <div className={styles.choiceCardTitle}>{meta.title}</div>
                                        <div className={styles.choiceCardDesc}>{meta.desc}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className={styles.choiceBack}>
                    <button className={styles.choiceBackBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft /> Quay lại danh sách
                    </button>
                </div>
            </div>
        );
    }

    // Nếu là combo → dùng form riêng
    if (type === 'combo') {
        return <ComboCreateForm />;
    }

    // Nếu là dichvu hoặc sanpham → dùng form chung
    return <ServiceCreateForm typeService={type} />;
}
